import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { serviceActions } from '../../state/modules/service';
import { reProcess } from '../../utils/BackgroundJobs';
import { change, submit } from '../../components/connectedForm/utils/actions';
import { findPathByUuId } from '../../utils/UuIdGenerator';
import DeprecatedConfirmationPrompt from '../../components/modals/ConfirmationPrompt';
import { Maybe } from '../../graphql/schema/p4types';
import FluentModal from '../../components/modals/FluentModal';

type ICustomPromptHandlers = {
  [confirmationKey: string]: Maybe<{ [confirmationChoice: string]: Maybe<() => Promise<any>> }>;
};

const AppConfirmationPrompt = ({ customPromptHandlers }: { customPromptHandlers: ICustomPromptHandlers }) => {
  const confirmationPrompt = useSelector((store: any) => store.data.alert.confirmationPrompt);
  const newform = useSelector((store: any) => store.newform);
  const dispatch = useDispatch();

  const clearConfirmationPrompt = () => {
    dispatch(serviceActions.dismissConfirmationPrompt());
  };

  //Standard 'Yes' response handling
  const confirmConfirmationPrompt = () => {
    if (typeof confirmationPrompt.confirmationReference === 'function') {
      confirmationPrompt.confirmationReference();
    } else if (confirmationPrompt.mutationProps) {
      reProcess(confirmationPrompt);
    } else {
      let field = `confirmationResponses.${[confirmationPrompt.confirmationReference]}`;
      let completeAction = submit;

      //Do not change to confirmationPrompt.form.type! The issue is elsewhere.
      //(Change has been made and reverted 3 times - Thought I'd best add a message about it).
      field = findPathByUuId(newform[confirmationPrompt.form].values, confirmationPrompt.uuid) + field;
      Promise.resolve(dispatch(change(confirmationPrompt.form, field, true, false))).then(() => {
        dispatch(completeAction(confirmationPrompt.form));
      });
    }
    clearConfirmationPrompt();
  };

  //Multi-Option Response Handling.
  const confirmPromptResponse = (value: any) => {
    let field = `confirmationResponses.${[confirmationPrompt.confirmationReference]}`;
    field = findPathByUuId(newform[confirmationPrompt.form].values, confirmationPrompt.uuid) + field;

    const customHandler = customPromptHandlers?.[field]?.[value];

    if (customHandler) {
      //If we have a custom prompt handler, then we need to execute that logic before we continue with re-save.
      //Note: Expecting the custom logic to trigger its' own re-save (Payment addition does this via a useEffect on newPaymentLines changing).
      Promise.resolve(dispatch(change(confirmationPrompt.form, field, value, false))).then(customHandler);
    } else {
      Promise.resolve(dispatch(change(confirmationPrompt.form, field, value, false))).then(() => {
        dispatch(submit(confirmationPrompt.form));
      });
    }

    clearConfirmationPrompt();
  };

  const responseCount = (confirmationPrompt.responses && confirmationPrompt.responses.length) || 2;
  return (
    <FluentModal isOpen={true}>
      <div style={{ width: `${350 + responseCount * 100}px`, minHeight: '200px', padding: '1em' }}>
        <DeprecatedConfirmationPrompt
          onReject={clearConfirmationPrompt}
          title="Confirmation Required"
          confirm={confirmConfirmationPrompt}
          message={confirmationPrompt.general}
          confirmationResponses={confirmationPrompt.responses}
          onConfirmPromptResponse={confirmPromptResponse}
        />
      </div>
    </FluentModal>
  );
};

/**
 * Extracted confirmation prompt handling logic from App.js for re-location under ConnectedForm.js
 * Handles confirmationResponses for regular form saves & re-submits once a selection has been made.
 * @param customPromptHandlers - Custom prompt handlers can be provided for advanced functionality (Such as displaying an additional modal for user input, like payments).
 */
export default ({ customPromptHandlers }: { customPromptHandlers: ICustomPromptHandlers }) => {
  const confirmationPrompt = useSelector((store: any) => store.data.alert.confirmationPrompt);

  if (confirmationPrompt && confirmationPrompt.status === 'CONFIRMATION_REQUIRED') {
    return <AppConfirmationPrompt customPromptHandlers={customPromptHandlers} />;
  }
  return null;
};
